import Utils from '../../../utils/immersive_utils';
import { computeDailyStats } from '../../portfolio-performance/compute-daily-stats';
import {
  IPortfolioPerformanceAccountReport,
  IPortfolioPerformanceHistorical,
} from '../../portfolio-performance/portfolio-performance.types';
import { BacktesterScenario } from '../api/backtester-config-api.types';

const MONTH_ORDER = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

const fmtPct = (value: number | null): string | null =>
  value === null ? null : `${value >= 0 ? '+' : ''}${(value * 100).toFixed(2)}%`;

const fmtRatio = (value: number | null): string | null =>
  value === null ? null : value.toFixed(2);

// PV is reported in USD for now. The backtester's per-snapshot `pv` is a
// portfolio value whose denomination isn't self-describing in the payload
// (execution config carries convert_*_to_usd / options_settle_in_usd flags
// that determine it) — see the "denomination" open question in the reuse
// plan. USD is the safe default for current runs; isolated here so it's a
// one-line change once William confirms the intended convention.
const DEFAULT_DENOMINATION = 'USD';

// Extract the UTC calendar day ('YYYY-MM-DD') from a snapshot timestamp.
// Split the date portion off the string directly rather than parsing via
// `new Date` and reading local components: a zoneless string like
// "2026-01-01 00:00:00" parsed as local time would land on the wrong
// calendar day for any host west of UTC. Ordering (below) still goes through
// Date, which is safe because a constant TZ offset preserves order even if
// the absolute instant is off.
const utcDayKeyOf = (timestamp: string): string | null => {
  const datePart = timestamp.split(/[T ]/)[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : null;
};

const formatNpv = (pv: number, denomination: string): string =>
  denomination === 'USD' || denomination === 'USDC'
    ? Utils.valueToCurrency(pv, 2)
    : Utils.formatNumber(pv, 4);

// Reshapes a single completed backtest scenario into the same
// IPortfolioPerformanceAccountReport shape a live account produces, so a
// backtest stratlet flows through the Portfolio Performance account card /
// Overview table / equity-underwater charts unchanged. Mirrors
// build-reference-report.ts (the synthetic "Buy and Hold" reference row),
// which does exactly this for a plain daily-close series.
//
// Two deliberate divergences from a live account, both honest:
//   - Positions/capital/description are nulled. The backtester's per-snapshot
//     output carries no point-in-time position book (blotters/closed_structures
//     are unpopulated by default and of unknown runtime shape today), so the
//     Positions tab is disabled for backtest rows, exactly as it is for
//     reference rows. Revisit if reporting flags get turned on (see plan).
//   - daily/mtd/ytd are anchored on the LAST day of the backtest WINDOW, not
//     on wall-clock "now". A backtest window sits in the past, so a now-anchored
//     MTD/YTD (as the live-account reference builder uses) would be null or
//     nonsensical. Window-relative is the meaningful reading here.
//
// Sharpe/Sortino/MaxDD come from the shared computeDailyStats over the derived
// daily series — NOT scenario.statistics — so a backtest row in the Overview
// table means the same thing as every live-account and reference row there
// (the account card recomputes live-account stats the same way).
export const buildBacktestReport = (
  scenario: BacktesterScenario,
  stratletName: string,
): IPortfolioPerformanceAccountReport | null => {
  // Downsample intraday snapshots to one PV per UTC day: last snapshot of the
  // day == that day's "close". This matches the daily grain the PP calendar /
  // equity chart assume (their historical.days only ever holds closed days).
  // Chosen default for the frequency-mismatch question in the reuse plan; if
  // William wants a different daily anchor (e.g. midnight snapshot) it's a
  // localized change to this reduction only.
  const rows = [...scenario.pnl_reports]
    .filter(
      (r) => utcDayKeyOf(r.timestamp) !== null && typeof r.pv === 'number' && Number.isFinite(r.pv),
    )
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const pvByDay = new Map<string, number>();
  for (const r of rows) {
    // Sorted ascending, so the last write for a given day wins the value.
    pvByDay.set(utcDayKeyOf(r.timestamp) as string, r.pv);
  }

  // 'YYYY-MM-DD' sorts lexicographically in chronological order.
  const dayKeys = [...pvByDay.keys()].sort();
  if (dayKeys.length < 2) return null;

  const historical: IPortfolioPerformanceHistorical = {};

  for (let i = 1; i < dayKeys.length; i++) {
    const prevPv = pvByDay.get(dayKeys[i - 1]) as number;
    const pv = pvByDay.get(dayKeys[i]) as number;
    const [year, monthNum, day] = dayKeys[i].split('-');
    const monthKey = MONTH_ORDER[Number(monthNum) - 1];
    const dailyReturn = prevPv !== 0 ? pv / prevPv - 1 : 0;

    historical[year] ??= { annual_return: null, months: {} };
    historical[year].months[monthKey] ??= { monthly_return: 0, days: {} };
    historical[year].months[monthKey].days[day] = { return: dailyReturn, pv };
  }

  for (const yearData of Object.values(historical)) {
    let yearCompound = 1;
    for (const monthData of Object.values(yearData.months)) {
      const monthCompound = Object.values(monthData.days).reduce(
        (acc, d) => acc * (1 + d.return),
        1,
      );
      monthData.monthly_return = monthCompound - 1;
      yearCompound *= monthCompound;
    }
    yearData.annual_return = yearCompound - 1;
  }

  const stats = computeDailyStats(historical);

  const lastDayKey = dayKeys[dayKeys.length - 1];
  const [lastYear, lastMonthNum] = lastDayKey.split('-');
  const lastMonthKey = MONTH_ORDER[Number(lastMonthNum) - 1];
  const lastPv = pvByDay.get(lastDayKey) as number;
  const prevPv = pvByDay.get(dayKeys[dayKeys.length - 2]) as number;
  const lastReturn = prevPv !== 0 ? lastPv / prevPv - 1 : 0;
  const mtdReturn = historical[lastYear]?.months[lastMonthKey]?.monthly_return ?? null;
  const ytdReturn = historical[lastYear]?.annual_return ?? null;

  return {
    team: 'Backtest',
    strategy: stratletName,
    description: null,
    // No point-in-time position book in backtester output today — Positions
    // tab is disabled for backtest rows (see header comment).
    positions: [],
    positionsTotalPv: null,
    capital: null,
    npv: formatNpv(lastPv, DEFAULT_DENOMINATION),
    daily: fmtPct(lastReturn),
    mtd: fmtPct(mtdReturn),
    ytd: fmtPct(ytdReturn),
    maxDD: fmtPct(stats.maxDrawdownPct),
    sharpe: fmtRatio(stats.sharpe),
    sortino: fmtRatio(stats.sortino),
    denomination: DEFAULT_DENOMINATION,
    historical,
    ytdReturnRaw: ytdReturn,
    dailyReturnRaw: lastReturn,
    mtdReturnRaw: mtdReturn,
    pvRaw: lastPv,
    sharpeRaw: stats.sharpe,
    sortinoRaw: stats.sortino,
    maxDDRaw: stats.maxDrawdownPct,
    // A backtest is a one-shot run, not a periodically-recomputed live account,
    // so there's no meaningful "calculated at" batch timestamp to surface.
    calculatedAt: null,
  };
};
